import { PERMISSIONS } from '../../shared/constants/permissions';
import { PARKTEK_INTERNAL_ROLES, ROLES } from '../../shared/constants/roles';
import { CONTROLLER_FULL_DETAIL_ROLES } from '../../features/controllers/controllerAccess';

export const routeConfig = [
  // Auth routes (public)
  {
    path: '/login',
    type: 'login',
    lazy: () => import('../../features/auth/pages/Login'),
  },
  {
    path: '/forgot-password',
    type: 'public',
    lazy: () => import('../../features/auth/pages/ForgotPassword'),
  },
  {
    path: '/verify-email',
    type: 'public',
    lazy: () => import('../../features/auth/pages/VerifyEmail'),
  },
  {
    path: '/reset-password',
    type: 'public',
    lazy: () => import('../../features/auth/pages/ResetPassword'),
  },
  {
    path: '/accept-invite',
    type: 'public',
    lazy: () => import('../../features/auth/pages/AcceptInvite'),
  },

  // Support
  {
    path: '/support',
    type: 'secured',
    permission: PERMISSIONS.SUPPORT_VIEW,
    title: 'Support Tickets',
    lazy: () => import('../../features/support/pages/SupportPage'),
  },

  // Contact Inquiries
  {
    path: '/contact-inquiries/:id',
    type: 'secured',
    permission: PERMISSIONS.CONTACT_INQUIRIES_VIEW,
    allowedRoles: [ROLES.SUPER_ADMIN, ROLES.ACCOUNT_MANAGER],
    title: 'Contact Inquiry',
    lazy: () => import('../../features/contact-inquiries/pages/ContactInquiryDetailPage'),
  },
  {
    path: '/contact-inquiries',
    type: 'secured',
    permission: PERMISSIONS.CONTACT_INQUIRIES_VIEW,
    allowedRoles: [ROLES.SUPER_ADMIN, ROLES.ACCOUNT_MANAGER],
    title: 'Contact Inquiries',
    lazy: () => import('../../features/contact-inquiries/pages/ContactInquiriesPage'),
  },

  // Account
  {
    path: '/account/profile',
    type: 'secured',
    permission: PERMISSIONS.SETTINGS_VIEW,
    title: 'Profile',
    lazy: () => import('../../features/settings/pages/AccountProfilePage'),
  },
  {
    path: '/account/security',
    type: 'secured',
    permission: PERMISSIONS.SETTINGS_VIEW,
    title: 'Security',
    lazy: () => import('../../features/settings/pages/AccountSecurityPage'),
  },
  {
    path: '/account/preferences',
    type: 'secured',
    permission: PERMISSIONS.SETTINGS_VIEW,
    title: 'Preferences',
    lazy: () => import('../../features/settings/pages/AccountPreferencesPage'),
  },
  {
    path: '/account/notifications',
    type: 'secured',
    permission: PERMISSIONS.SETTINGS_VIEW,
    title: 'Notifications',
    lazy: () => import('../../features/settings/pages/AccountNotificationsPage'),
  },

  // Society Admin
  {
    path: '/society-admin/team',
    type: 'secured',
    permission: 'society.team.read',
    title: 'Team & Access',
    lazy: () => import('../../features/settings/pages/SocietyTeamPage'),
  },
  {
    path: '/society-admin/roles',
    type: 'secured',
    permission: 'society.roles.read',
    title: 'Society Roles',
    lazy: () => import('../../features/settings/pages/SocietyRolesPage'),
  },
  {
    path: '/society-admin/roles/:id',
    type: 'secured',
    permission: 'society.roles.read',
    title: 'Society Role',
    lazy: () => import('../../features/settings/pages/SocietyRoleDetailPage'),
  },
  {
    path: '/society-admin/operations',
    type: 'secured',
    permission: 'society.settings.operations.read',
    title: 'Operations',
    lazy: () => import('../../features/settings/pages/SocietyOperationsPage'),
  },
  {
    path: '/society-admin/guard-policy',
    type: 'secured',
    permission: 'society.settings.guardPolicy.read',
    title: 'Guard Policy',
    lazy: () => import('../../features/settings/pages/SocietyGuardPolicyPage'),
  },

  // Platform Admin
  {
    path: '/platform-admin/iam',
    type: 'secured',
    permission: 'platform.iam.admin.read',
    title: 'Platform IAM',
    lazy: () => import('../../features/settings/pages/PlatformIamPage'),
  },
  {
    path: '/reports',
    type: 'secured',
    permission: PERMISSIONS.LOGS_VIEW,
    title: 'Reports',
    lazy: () => import('../../features/reports/pages/ReportsPage'),
  },
  {
    path: '/anomalies',
    type: 'secured',
    permission: PERMISSIONS.LOGS_VIEW,
    allowedRoles: PARKTEK_INTERNAL_ROLES,
    title: 'Anomalies',
    lazy: () => import('../../features/anomalies/pages/AnomaliesPage'),
  },
  {
    path: '/portfolio',
    type: 'secured',
    permission: PERMISSIONS.ONBOARDING_VIEW,
    title: 'My Portfolio',
    lazy: () => import('../../features/portfolio/pages/PortfolioPage'),
  },
  {
    path: '/technician-tasks',
    type: 'secured',
    permission: PERMISSIONS.CONTROLLERS_VIEW,
    allowedRoles: [ROLES.SUPER_ADMIN, ROLES.DEVELOPER, ROLES.ACCOUNT_MANAGER],
    title: 'Technician Tasks',
    lazy: () => import('../../features/technician-tasks/pages/TechnicianTasksPage'),
  },
  {
    path: '/platform-admin/roles-matrix',
    type: 'secured',
    permission: 'platform.iam.admin.read',
    title: 'Roles & Permissions',
    lazy: () => import('../../features/settings/pages/RolePermissionMatrixPage'),
  },
  {
    path: '/platform-admin/platform-policies',
    type: 'secured',
    permission: 'platform.settings.policy.read',
    title: 'Platform Policies',
    lazy: () => import('../../features/settings/pages/PlatformPoliciesPage'),
  },
  {
    path: '/platform-admin/app-versions',
    type: 'secured',
    permission: 'platform.appVersions.read',
    title: 'App Versions',
    lazy: () => import('../../features/settings/pages/PlatformAppVersionsPage'),
  },
  {
    path: '/platform-admin/operation-blocks',
    type: 'secured',
    permission: 'platform.operationBlocks.read',
    title: 'Operation Blocks',
    lazy: () => import('../../features/settings/pages/PlatformOperationBlocksPage'),
  },

  // Settings
  {
    path: '/settings',
    type: 'redirect',
    permission: PERMISSIONS.SETTINGS_VIEW,
    to: '/account/profile',
  },
  {
    path: '/settings/profile',
    type: 'redirect',
    permission: PERMISSIONS.SETTINGS_VIEW,
    to: '/account/profile',
  },
  {
    path: '/settings/security',
    type: 'redirect',
    permission: PERMISSIONS.SETTINGS_VIEW,
    to: '/account/security',
  },
  {
    path: '/settings/preferences',
    type: 'redirect',
    permission: PERMISSIONS.SETTINGS_VIEW,
    to: '/account/preferences',
  },
  {
    path: '/settings/notifications',
    type: 'redirect',
    permission: PERMISSIONS.SETTINGS_VIEW,
    to: '/account/notifications',
  },
  {
    path: '/settings/team-access',
    type: 'secured',
    permission: ['platform.iam.admin.read', 'society.team.read'],
    title: 'Team & Access',
    lazy: () =>
      import('../../features/settings/pages/SettingsLegacyRedirect').then((module) => ({
        default: module.SettingsTeamAccessLegacyRedirect,
      })),
  },
  {
    path: '/settings/operations',
    type: 'secured',
    permission: [
      PERMISSIONS.SETTINGS_SOCIETY_OPERATIONS_VIEW,
      PERMISSIONS.SETTINGS_GUARD_POLICY_VIEW,
      PERMISSIONS.OPERATION_BLOCKS_VIEW,
    ],
    title: 'Operations',
    lazy: () =>
      import('../../features/settings/pages/SettingsLegacyRedirect').then((module) => ({
        default: module.SettingsOperationsLegacyRedirect,
      })),
  },
  {
    path: '/settings/app-versions',
    type: 'redirect',
    permission: PERMISSIONS.APP_VERSION_POLICIES_VIEW,
    to: '/platform-admin/app-versions',
  },
  {
    path: '/settings/platform-policies',
    type: 'redirect',
    permission: PERMISSIONS.SETTINGS_PLATFORM_VIEW,
    to: '/platform-admin/platform-policies',
  },
  {
    path: '/settings/controller-settings',
    type: 'redirect',
    permission: PERMISSIONS.CONTROLLERS_MANAGE,
    allowedRoles: [ROLES.SUPER_ADMIN, ROLES.DEVELOPER, ROLES.INSTALLATION_MANAGER],
    to: '/controllers',
  },
  {
    path: '/settings/*',
    type: 'secured',
    permission: PERMISSIONS.SETTINGS_VIEW,
    title: 'Settings',
    lazy: () => import('../../features/settings/pages/SettingsPage'),
  },

  // Onboarding
  {
    path: '/onboarding',
    type: 'secured',
    permission: PERMISSIONS.ONBOARDING_VIEW,
    allowedRoles: [ROLES.SUPER_ADMIN, ROLES.DEVELOPER, ROLES.ACCOUNT_MANAGER],
    title: 'Onboarding',
    lazy: () => import('../../features/onboarding/OnboardingBoardPage'),
  },

  // Societies
  {
    path: '/societies',
    type: 'secured',
    permission: PERMISSIONS.SOCIETIES_VIEW,
    title: 'Societies',
    lazy: () => import('../../features/societies/pages/SocietiesPage'),
  },
  {
    path: '/societies/:id',
    type: 'secured',
    permission: PERMISSIONS.SOCIETIES_MANAGE,
    title: 'Societies',
    lazy: () => import('../../features/societies/pages/SocietyDetailPage'),
  },

  // Society Admins redirect
  {
    path: '/society-admins',
    type: 'redirect',
    permission: PERMISSIONS.SETTINGS_TEAM_VIEW,
    to: '/society-admin/team',
  },

  // Users
  {
    path: '/users',
    type: 'secured',
    permission: PERMISSIONS.USERS_VIEW,
    title: 'Users',
    lazy: () => import('../../features/users/pages/UsersListPage'),
  },
  {
    path: '/users/:id',
    type: 'secured',
    permission: PERMISSIONS.USERS_VIEW,
    title: 'User Detail',
    lazy: () => import('../../features/users/pages/UserDetailPage'),
  },
  {
    path: '/thirdparty',
    type: 'secured',
    permission: PERMISSIONS.USERS_VIEW,
    title: 'Third Party',
    lazy: () => import('../../features/thirdparty/pages/ThirdPartyPage'),
  },

  // Vehicles
  {
    path: '/vehicles',
    type: 'secured',
    permission: PERMISSIONS.USERS_VIEW,
    title: 'Vehicles',
    lazy: () => import('../../features/vehicles/pages/VehiclesPage'),
  },

  // Inventory
  {
    path: '/inventory',
    type: 'secured',
    permission: PERMISSIONS.INVENTORY_SOCIETY_VIEW,
    requireSociety: true,
    title: 'Tag Inventory',
    lazy: () => import('../../features/inventory/pages/InventorySocietyPage'),
  },
  {
    path: '/inventory/global',
    type: 'secured',
    permission: PERMISSIONS.INVENTORY_GLOBAL_VIEW,
    title: 'Tags Inventory Global',
    lazy: () => import('../../features/inventory/pages/InventoryGlobalPage'),
  },
  {
    path: '/inventory/allocate',
    type: 'redirect',
    permission: PERMISSIONS.INVENTORY_GLOBAL_MANAGE,
    to: '/inventory/global?dialog=allocate',
  },

  // Logs
  {
    path: '/logs',
    type: 'secured',
    permission: PERMISSIONS.LOGS_VIEW,
    title: 'Logs',
    lazy: () => import('../../features/logs/pages/LogsPage'),
  },
  {
    path: '/anpr-visitor-logs',
    type: 'secured',
    permission: PERMISSIONS.LOGS_VIEW,
    title: 'ANPR Visitor Logs',
    lazy: () => import('../../features/logs/pages/AnprVisitorLogsPage'),
  },
  {
    path: '/alpr-accuracy',
    type: 'secured',
    permission: PERMISSIONS.LOGS_VIEW,
    allowedRoles: [ROLES.SUPER_ADMIN, ROLES.DEVELOPER],
    title: 'ALPR Accuracy',
    lazy: () => import('../../features/alpr-accuracy/pages/AlprAccuracyPage'),
  },
  {
    path: '/boom-barrier-logs',
    type: 'secured',
    permission: PERMISSIONS.LOGS_VIEW,
    allowedRoles: [ROLES.SUPER_ADMIN, ROLES.DEVELOPER, ROLES.SOCIETY_ADMIN],
    title: 'Push Button Activity Logs',
    lazy: () => import('../../features/logs/pages/BoomBarrierLogsPage'),
  },
  {
    path: '/audit-logs',
    type: 'secured',
    permission: PERMISSIONS.AUDIT_LOGS_VIEW,
    allowedRoles: [ROLES.SUPER_ADMIN, ROLES.DEVELOPER],
    title: 'Audit Logs',
    lazy: () => import('../../features/audit-logs/pages/AuditLogsPage'),
  },

  // Controllers
  {
    path: '/controllers',
    type: 'secured',
    permission: PERMISSIONS.CONTROLLERS_VIEW,
    allowedRoles: [
      ROLES.SUPER_ADMIN,
      ROLES.DEVELOPER,
      ROLES.ACCOUNT_MANAGER,
      ROLES.SOCIETY_ADMIN,
      ROLES.SOCIETY_MANAGER,
      ROLES.SOCIETY_GUARD,
      ROLES.TECHNICIAN,
      ROLES.INSTALLATION_MANAGER,
    ],
    title: 'Controllers',
    lazy: () => import('../../features/controllers/pages/ControllersPage'),
  },
  {
    path: '/fleet-health',
    type: 'secured',
    permission: PERMISSIONS.CONTROLLERS_VIEW,
    allowedRoles: [ROLES.SUPER_ADMIN, ROLES.DEVELOPER],
    title: 'Device Operations',
    lazy: () => import('../../features/controllers/pages/NativeSsmPage'),
  },
  {
    path: '/fleet-health/ssm',
    type: 'secured',
    permission: PERMISSIONS.REMOTE_ACCESS_SSH,
    allowedRoles: [ROLES.SUPER_ADMIN, ROLES.DEVELOPER],
    title: 'SSM Maintenance',
    lazy: () => import('../../features/controllers/pages/NativeSsmPage'),
  },
  {
    path: '/fleet-health/rollout',
    type: 'secured',
    permission: PERMISSIONS.CONTROLLERS_VIEW,
    allowedRoles: [ROLES.SUPER_ADMIN, ROLES.DEVELOPER],
    title: 'Image Rollouts',
    lazy: () => import('../../features/controllers/pages/NativeSsmPage'),
  },
  {
    path: '/controllers/new',
    type: 'secured',
    permission: PERMISSIONS.CONTROLLERS_MANAGE,
    allowedRoles: [ROLES.SUPER_ADMIN, ROLES.DEVELOPER, ROLES.INSTALLATION_MANAGER],
    title: 'Create Controller',
    lazy: () => import('../../features/controllers/pages/CreateController'),
  },
  {
    path: '/controllers/:id/edit',
    type: 'secured',
    permission: PERMISSIONS.CONTROLLERS_MANAGE,
    allowedRoles: [ROLES.SUPER_ADMIN, ROLES.DEVELOPER, ROLES.INSTALLATION_MANAGER],
    title: 'Edit Controller',
    lazy: () => import('../../features/controllers/pages/CreateController'),
  },
  {
    path: '/controllers/:id/remote-access',
    type: 'secured',
    permission: PERMISSIONS.REMOTE_ACCESS_SSH,
    allowedRoles: [ROLES.SUPER_ADMIN, ROLES.DEVELOPER, ROLES.INSTALLATION_MANAGER],
    title: 'Controller Remote Access',
    lazy: () => import('../../features/controllers/pages/NativeSsmPage'),
  },
  {
    path: '/controllers/:id',
    type: 'secured',
    permission: PERMISSIONS.CONTROLLERS_VIEW,
    allowedRoles: CONTROLLER_FULL_DETAIL_ROLES,
    title: 'Controller Detail',
    lazy: () => import('../../features/controllers/pages/ControllerDetailPage'),
  },

  // Catch-all
  {
    path: '/',
    type: 'home',
  },
  {
    path: '*',
    type: 'home',
  },
];
